import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { statesOptions } from 'src/app/data/states';
import { SignupService } from 'src/app/services/signup/signup.service';

/** Mapa sigla UF (ex: ES, SP) para id do estado usado no select (1-27) */
const UF_SIGLA_TO_ID: Record<string, string> = {
  AC: '1',
  AL: '2',
  AP: '3',
  AM: '4',
  BA: '5',
  CE: '6',
  DF: '7',
  ES: '8',
  GO: '9',
  MA: '10',
  MT: '11',
  MS: '12',
  MG: '13',
  PA: '14',
  PB: '15',
  PR: '16',
  PE: '17',
  PI: '18',
  RJ: '19',
  RN: '20',
  RS: '21',
  RO: '22',
  RR: '23',
  SC: '24',
  SP: '25',
  SE: '26',
  TO: '27',
};
import { UtilsService } from 'src/app/services/utils/utils.service';

import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import Utils, {
  GetHelper,
  payloadsEqual,
  UtilsValidators,
} from 'src/app/helpers/utils';

@Component({
  selector: 'app-signup-unified-company-complementary-address',
  templateUrl: './signup-unified-company-complementary-address.component.html',
  styleUrls: ['./signup-unified-company-complementary-address.component.scss'],
})
export class SignupUnifiedCompanyComplementaryAddressComponent
  implements OnInit, OnDestroy
{
  form = this.formBuilder.group({
    cep: [null, [Validators.required, UtilsValidators.cep]],
    public_place: [null, [Validators.required]],
    number: [null, [Validators.required]],
    complement: [null, []],
    district: [null, [Validators.required]],
    city: [null, [Validators.required]],
    uf: [null, [Validators.required]],
    country: [null, [Validators.required]],
  });

  zipcodeFetched = false;

  addressObj: AddressProps = {
    public_place: '',
    district: '',
    city: '',
    uf: '',
    country: '',
  };

  states: SelectItem[] = statesOptions;

  isForeign = false;

  loading = false;

  data = null;

  event: any;

  loadingContent = false;

  private _updatingFormStatus = false;
  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, unknown> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    public signupService: SignupService,
    private utilsService: UtilsService,
    private maskPipe: MaskPipe,
    private authService: AuthService,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.setForeign();

    if (!this.form.controls.country.value) {
      this.form.controls.country.setValue('23', { emitEvent: false });
    }
    this.applyCountryRules(this.form.controls.country.value);

    // Sempre exibir todos os campos de endereço (não exige busca por CEP para “liberar”);
    // botão Seguir libera quando CEP e demais obrigatórios estiverem preenchidos e válidos
    this.zipcodeFetched = true;

    this.getDataAndFillForm();

    this.form.controls.country.valueChanges.subscribe((country) => {
      this.applyCountryRules(country);
      this.updateFormStatus();
    });

    this.event = this.signupService.sendComplementaryCompanyData.subscribe(
      (value) => {
        if (value === 'complementaryAddress') {
          this.sendData();
        }
      },
    );

    // Verificar se o CEP já está no formato correto e liberar os campos
    this.form.controls.cep.valueChanges.subscribe((value) => {
      if (value && value.length === 9) {
        // CEP está no formato correto (00000-000), liberar campos mesmo sem buscar
        if (!this.zipcodeFetched) {
          this.zipcodeFetched = true;
        }
      }
    });

    this.form.valueChanges.subscribe(() => {
      this.updateFormStatus();
    });
    this.form.statusChanges.subscribe(() => {
      this.updateFormStatus();
    });

    this.form.controls.country.valueChanges.subscribe((value) => {
      const isBrazil = String(value) === '23';
      this.applyAddressValidatorsByCountry(isBrazil);
      this.updateFormStatus();
    });

    this.signupService.fillFormEvent.subscribe(() => {
      if (!this.zipcodeFetched) {
        const zipcode = fakerbr.address.zipCodeValidByState('SP');

        const formatted = this.maskPipe.transform(zipcode, '00000-000');

        this.form.patchValue({
          cep: formatted,
          number: fakerbr.random.number({ min: 1, max: 1000 }),
          complement: 'Casa',
          country: '23',
        });

        this.handleSearchZipcode({
          target: { value: formatted },
        });

        this.zipcodeFetched = true;
      }
    });

    this.applyAddressValidatorsByCountry(
      String(this.form.controls.country.value) === '23',
    );
  }

  setForeign() {
    if (this.authService.user?.nationality?.country !== 'Brasil') {
      this.isForeign = true;
    }
  }

  /** Considera Brasil tanto valor numérico 23 quanto string '23' (select pode retornar qualquer um). */
  private isBrazilCountry(country: any): boolean {
    if (country == null || country === '') return false;
    const v = String(country).trim();
    return v === '23' || (country as number) === 23;
  }

  private applyCountryRules(country: any) {
    const isBrazil = this.isBrazilCountry(country);
    this.isForeign = !isBrazil;
  }
  isCountryBrazil(): boolean {
    return String(this.form?.get('country')?.value) === '23';
  }

  private clearCepIncompleteError() {
    const currentErrors = this.form.controls.cep.errors;
    if (currentErrors && currentErrors['cepIncomplete']) {
      const err = { ...currentErrors };
      delete err['cepIncomplete'];
      this.form.controls.cep.setErrors(Object.keys(err).length ? err : null);
    }
  }

  private applyAddressValidatorsByCountry(isBrazil: boolean) {
    if (isBrazil) {
      this.form.controls.cep.setValidators([
        Validators.required,
        UtilsValidators.cep,
      ]);
      this.form.controls.public_place.setValidators([Validators.required]);
      this.form.controls.number.setValidators([Validators.required]);
      this.form.controls.district.setValidators([Validators.required]);
      this.form.controls.city.setValidators([Validators.required]);
      this.form.controls.uf.setValidators([Validators.required]);
    } else {
      this.clearCepIncompleteError();
      this.form.controls.cep.setValidators([UtilsValidators.cep]);
      this.form.controls.public_place.setValidators([]);
      this.form.controls.number.setValidators([]);
      this.form.controls.district.setValidators([]);
      this.form.controls.city.setValidators([]);
      this.form.controls.uf.setValidators([]);
      this.form.controls.uf.setValue(null, { emitEvent: false });
    }

    this.form.controls.cep.updateValueAndValidity({ emitEvent: false });
    this.form.controls.public_place.updateValueAndValidity({
      emitEvent: false,
    });
    this.form.controls.number.updateValueAndValidity({ emitEvent: false });
    this.form.controls.district.updateValueAndValidity({ emitEvent: false });
    this.form.controls.city.updateValueAndValidity({ emitEvent: false });
    this.form.controls.uf.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Libera o botão Seguir quando CEP tem 8 dígitos e todos os obrigatórios
   * estão preenchidos (não depende de .valid dos selects).
   */
  updateFormStatus() {
    if (this._updatingFormStatus) return;
    if (this.loading) {
      this.signupService.setCompanyFormStatus('complementaryAddress', false);
      return;
    }
    const isBrazil = String(this.form.controls.country.value) === '23';
    const cepValue = this.form.controls.cep.value;
    const cepDigits = cepValue ? Utils.onlyNumbers(String(cepValue)).length : 0;
    const cepValid = !isBrazil || cepDigits === 8;
    if (isBrazil && cepDigits > 0 && cepDigits < 8) {
      this.form.controls.cep.setErrors({ cepIncomplete: true });
      this.form.controls.cep.markAsTouched();
    } else if (!isBrazil || cepDigits === 8) {
      this.clearCepIncompleteError();
    }
    const publicPlaceFilled = !!(
      this.form.controls.public_place.value &&
      String(this.form.controls.public_place.value).trim()
    );
    const numberFilled = !!(
      this.form.controls.number.value != null &&
      this.form.controls.number.value !== '' &&
      String(this.form.controls.number.value).trim()
    );
    const districtFilled = !!(
      this.form.controls.district.value &&
      String(this.form.controls.district.value).trim()
    );
    const cityFilled = !!(
      this.form.controls.city.value &&
      String(this.form.controls.city.value).trim()
    );
    const ufVal = this.form.controls.uf.value;
    const ufFilled =
      ufVal != null && ufVal !== '' && String(ufVal).trim() !== '';
    const countryVal = this.form.controls.country.value;
    const countryFilled =
      countryVal != null &&
      countryVal !== '' &&
      String(countryVal).trim() !== '';
    const allRequiredFilled = isBrazil
      ? cepValid &&
        publicPlaceFilled &&
        numberFilled &&
        districtFilled &&
        cityFilled &&
        ufFilled &&
        countryFilled
      : countryFilled;
    this.signupService.setCompanyFormStatus(
      'complementaryAddress',
      allRequiredFilled,
    );
  }

  async handleSearchZipcode(event: any) {
    if (String(this.form.controls.country.value) !== '23') {
      this.loading = false;
      this.updateFormStatus();
      return;
    }

    const zipcode = event.target.value;
    const isBrazil = this.isBrazilCountry(this.form.controls.country.value);

    if (!isBrazil) {
      this.loading = false;
      this.zipcodeFetched = true;
      this.updateFormStatus();
      return;
    }

    // Se o CEP está no formato correto (9 caracteres), liberar campos imediatamente
    if (zipcode && zipcode.length === 9) {
      this.zipcodeFetched = true;
    }

    this.loading = true;

    var obj = {
      public_place: '',
      district: '',
      city: '',
      uf: '',
      country: '',
    };

    try {
      const zipcodeRes = await this.utilsService.getZipcode(zipcode);

      if (zipcodeRes) {
        const foundCep = Object.keys(zipcodeRes).some((key) => {
          return key === 'partner_response' ? false : zipcodeRes[key] !== '';
        });

        if (foundCep) {
          const ufFromCep = zipcodeRes.uf;
          const ufValue =
            typeof ufFromCep === 'string' && ufFromCep.length === 2
              ? (UF_SIGLA_TO_ID[ufFromCep.toUpperCase()] ?? ufFromCep)
              : ufFromCep;
          obj = {
            public_place: zipcodeRes.logradouro,
            district: zipcodeRes.bairro,
            city: zipcodeRes.localidade,
            uf: ufValue,
            country: '23',
          };

          this.form.patchValue(obj);

          this.zipcodeFetched = true;
        } else {
          // Se não encontrou, manter os campos liberados se o CEP está no formato correto
          if (zipcode.length !== 9) {
            Utils.resetZipcodeFields(this.form);
            this.form.updateValueAndValidity();
            this.zipcodeFetched = false;
          } else {
            // CEP está no formato correto mas não foi encontrado, manter campos liberados
            this.zipcodeFetched = true;
          }
        }
      }
    } catch (error) {
      // Em caso de erro, se o CEP está no formato correto, manter campos liberados
      if (zipcode && zipcode.length === 9) {
        this.zipcodeFetched = true;
      } else {
        this.zipcodeFetched = false;
      }
      console.error(error);
    }

    this.addressObj = obj;

    this.loading = false;
    this.updateFormStatus();
  }

  async getDataAndFillForm() {
    this.loadingContent = true;

    try {
      // Sempre usar o register_uuid da URL
      const routeId = this.route.snapshot.paramMap.get('id');
      let apiRoute: string;
      let params: any = null;

      if (routeId) {
        apiRoute = 'api/registration/address/';
        params = { register_uuid: routeId };
      } else {
        apiRoute = 'api/registration/address/me/';
      }

      const response = await this.api.get<
        ApiResponse<AddressResponse | AddressResponse[]>
      >({
        route: apiRoute,
        params: params,
        token: true,
      });

      // A API retorna um array, então pegamos o endereço com is_main: true
      let addressData: AddressResponse;
      if (Array.isArray(response.data)) {
        // Se for array, pega o endereço com is_main: true ou o primeiro da lista
        addressData =
          response.data.find((addr: any) => addr.is_main === true) ||
          response.data[0];
      } else {
        addressData = response.data;
      }

      if (addressData) {
        const ufRaw = addressData.uf;
        const ufValue =
          typeof ufRaw === 'string' &&
          ufRaw.length === 2 &&
          /^[A-Za-z]{2}$/.test(ufRaw)
            ? (UF_SIGLA_TO_ID[ufRaw.toUpperCase()] ?? ufRaw)
            : ufRaw != null
              ? String(ufRaw)
              : null;
        this.form.patchValue({
          ...addressData,
          uf: ufValue,
          country: addressData.country.toString(),
        });

        this.addressObj = {
          public_place: addressData.public_place,
          district: addressData.district,
          city: addressData.city,
          uf: String(ufValue ?? addressData.uf),
          country: String(addressData.country),
        };

        this.data = addressData;
        this.initialPayload = this.buildPayloadFromForm();
      } else {
        this.initialPayload = null;
      }

      this.applyCountryRules(this.form.controls.country.value);

      // Sempre liberar os campos de endereço (com ou sem dados da API / 404):
      // usuário pode preencher manualmente; botão libera quando tudo obrigatório estiver válido
      this.zipcodeFetched = true;
      this.updateFormStatus();
    } catch (error) {
      this.initialPayload = null;
      if (error.status !== 404) {
        this.toast.show(
          'error',
          'Erro',
          error.error?.message || 'Erro ao carregar endereço',
        );
      }
      this.zipcodeFetched = true;
      this.updateFormStatus();
    } finally {
      this.loadingContent = false;
      setTimeout(() => this.updateFormStatus(), 0);
      setTimeout(() => this.updateFormStatus(), 200);
    }
  }

  private buildPayloadFromForm(): Record<string, unknown> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      cep: valuesHelper.get('cep'),
      public_place: valuesHelper.get('public_place'),
      number: valuesHelper.get('number'),
      complement: valuesHelper.get('complement'),
      district: valuesHelper.get('district'),
      city: valuesHelper.get('city'),
      uf: valuesHelper.get('uf'),
      country: Number(valuesHelper.get('country')),
      type: 1,
      address_type: 'user',
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');
    const payload = this.buildPayloadFromForm();

    if (
      payloadsEqual(
        payload as Record<string, any>,
        this.initialPayload ?? undefined,
      )
    ) {
      this.signupService.loading = false;
      this.signupService.setComplementaryCompanyStep('complementaryBank');
      this.signupService.changeCompanyStepEvent.emit('bank');
      return;
    }

    this.signupService.loading = true;

    try {
      if (this.data) {
        // PUT usa a rota com ID do endereço e register_uuid como query parameter
        let apiRoute: string;
        let params: any = null;

        if (routeId) {
          apiRoute = `api/registration/address/${this.data.id}/`;
          params = { register_uuid: routeId };
        } else {
          apiRoute = 'api/registration/address/me/';
        }

        const { data } = await this.api.put({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.data = data;
      } else {
        // POST usa a rota normal com register_uuid como query parameter
        let apiRoute: string;
        let params: any = null;

        if (routeId) {
          apiRoute = 'api/registration/address/';
          params = { register_uuid: routeId };
        } else {
          apiRoute = 'api/registration/address/me/';
        }

        const { data } = await this.api.post({
          route: apiRoute,
          params: params,
          token: true,
          body: payload,
        });

        this.data = data;
      }

      this.initialPayload = { ...payload };
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');

      this.signupService.setComplementaryCompanyStep('complementaryBank');
      this.signupService.changeCompanyStepEvent.emit('bank');
    } catch (error: any) {
      console.error('Erro ao salvar endereço:', error);

      // Tratar diferentes formatos de erro
      let errorMessage = 'Erro ao salvar endereço';

      if (error?.error) {
        if (typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.error?.data) {
          errorMessage =
            typeof error.error.data === 'string'
              ? error.error.data
              : JSON.stringify(error.error.data);
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      this.toast.show('error', 'Erro', errorMessage);
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }
}
